import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { classesAPI, SITE_NAMES, uploadAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, ArrowLeft, Pencil, Trash2, Users, GraduationCap, Upload, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export const ClassesListAdmin = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const response = await classesAPI.getAll(false);
      setClasses(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst třídy');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await classesAPI.delete(deleteId);
      setClasses(classes.filter(c => c.id !== deleteId));
      toast.success('Třída byla smazána');
    } catch (error) {
      toast.error('Nepodařilo se smazat třídu');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Třídy</h1>
          <p className="text-[#64748B]">Správa tříd mateřské školy</p>
        </div>
        <Link to="/admin/tridy/nova">
          <Button className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-class">
            <Plus size={18} className="mr-2" />
            Nová třída
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {classes.map((cls) => (
          <div key={cls.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`class-card-${cls.id}`}>
            {cls.image_url && (
              <img src={cls.image_url} alt={cls.name} className="w-full h-40 object-cover" />
            )}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <span className={`badge ${cls.is_active ? 'badge-success' : 'badge-warning'} mb-2`}>
                    {cls.is_active ? 'Aktivní' : 'Neaktivní'}
                  </span>
                  <h3 className="font-semibold text-lg text-[#2C3E50]">{cls.name}</h3>
                  <p className="text-sm text-[#64748B]">{cls.age_group}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to={`/admin/tridy/${cls.id}`} className="cursor-pointer">
                        <Pencil size={14} className="mr-2" />
                        Upravit
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDeleteId(cls.id)} className="cursor-pointer text-red-600">
                      <Trash2 size={14} className="mr-2" />
                      Smazat
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm text-[#64748B]">
                <span>{cls.teachers?.find(t => t.role === 'head_teacher')?.name || cls.teacher_name || '—'}</span>
                <span className="flex items-center gap-1">
                  <Users size={14} />
                  {cls.capacity}
                </span>
              </div>
            </div>
          </div>
        ))}
        
        {classes.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#64748B]">
            Žádné třídy
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat třídu?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const ClassFormAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    teacher_name: '',
    teachers: [],
    age_group: '',
    capacity: 20,
    image_url: '',
    is_active: true,
    site: 'ms'
  });

  useEffect(() => {
    if (isEdit) {
      loadClass();
    }
  }, [id]);

  const loadClass = async () => {
    try {
      const response = await classesAPI.getOne(id);
      setFormData({
        ...response.data,
        teachers: response.data.teachers || []
      });
    } catch (error) {
      toast.error('Nepodařilo se načíst třídu');
      navigate('/admin/tridy');
    } finally {
      setLoading(false);
    }
  };

  const addTeacher = () => {
    setFormData(prev => ({
      ...prev,
      teachers: [...(prev.teachers || []), { name: '', role: 'teacher' }]
    }));
  };

  const updateTeacher = (idx, field, value) => {
    setFormData(prev => {
      const teachers = [...(prev.teachers || [])];
      teachers[idx] = { ...teachers[idx], [field]: value };
      return { ...prev, teachers };
    });
  };

  const removeTeacher = (idx) => {
    setFormData(prev => ({
      ...prev,
      teachers: (prev.teachers || []).filter((_, i) => i !== idx)
    }));
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Vyfiltruj prázdné učitele a synchronizuj teacher_name (legacy pole) s prvním třídním učitelem
      const teachers = (formData.teachers || []).filter(t => t.name?.trim()).map(t => ({
        name: t.name.trim(),
        role: t.role || 'teacher'
      }));
      const headTeacher = teachers.find(t => t.role === 'head_teacher') || teachers[0];
      const payload = {
        ...formData,
        teachers,
        teacher_name: headTeacher?.name || formData.teacher_name || ''
      };
      if (isEdit) {
        await classesAPI.update(id, payload);
        toast.success('Třída byla aktualizována');
      } else {
        await classesAPI.create(payload);
        toast.success('Třída byla vytvořena');
      }
      navigate('/admin/tridy');
    } catch (error) {
      toast.error('Nepodařilo se uložit třídu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/tridy" className="text-[#64748B] hover:text-[#4ECDC4]">
          <ArrowLeft size={24} />
        </Link>
        <h1 className="text-2xl font-bold text-[#2C3E50]">
          {isEdit ? 'Upravit třídu' : 'Nová třída'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Název třídy *</Label>
            <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="rounded-xl" data-testid="class-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="age_group">Věková skupina *</Label>
            <Input id="age_group" name="age_group" value={formData.age_group} onChange={handleChange} required className="rounded-xl" placeholder="3-4 roky" data-testid="class-age" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="capacity">Kapacita *</Label>
            <Input id="capacity" name="capacity" type="number" min="1" value={formData.capacity} onChange={handleChange} required className="rounded-xl" data-testid="class-capacity" />
          </div>
        </div>

        <div className="space-y-3" data-testid="teachers-section">
          <div className="flex items-center justify-between">
            <Label>Pedagogický tým</Label>
            <Button type="button" size="sm" variant="outline" onClick={addTeacher} className="rounded-full" data-testid="add-teacher-btn">
              <Plus size={14} className="mr-1" /> Přidat učitele
            </Button>
          </div>
          {(formData.teachers || []).length === 0 && (
            <div className="text-sm text-[#64748B] text-center py-4 border border-dashed border-slate-200 rounded-xl">
              Zatím žádní učitelé. Klikněte na „Přidat učitele".
            </div>
          )}
          {(formData.teachers || []).map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl" data-testid={`teacher-row-${i}`}>
              <Input
                value={t.name || ''}
                onChange={(e) => updateTeacher(i, 'name', e.target.value)}
                placeholder="Jméno a příjmení"
                className="rounded-lg flex-1 min-w-[180px]"
                data-testid={`teacher-${i}-name`}
              />
              <Select value={t.role || 'teacher'} onValueChange={(v) => updateTeacher(i, 'role', v)}>
                <SelectTrigger className="rounded-lg w-[210px]" data-testid={`teacher-${i}-role`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="head_teacher">Třídní učitelka</SelectItem>
                  <SelectItem value="teacher">Učitelka</SelectItem>
                  <SelectItem value="assistant">Asistentka pedagoga</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeTeacher(i)} className="text-red-500 hover:text-red-700" data-testid={`teacher-${i}-remove`}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Popis *</Label>
          <Textarea id="description" name="description" value={formData.description} onChange={handleChange} required rows={4} className="rounded-xl resize-none" data-testid="class-description" />
        </div>

        <div className="space-y-2">
          <Label>Obrázek trídy</Label>
          <div className="flex gap-2 items-center">
            {formData.image_url && (
              <img src={formData.image_url} alt="Preview" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
            )}
            <label className="cursor-pointer flex-1">
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                setUploading(true);
                try { const res = await uploadAPI.upload(file); setFormData(prev => ({ ...prev, image_url: res.data.url })); }
                catch (err) { console.error(err); } finally { setUploading(false); }
              }} />
              <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : formData.image_url ? 'Zmenit obrázek' : 'Nahrát obrázek'}</span>
              </Button>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Sekce</Label>
          <Select value={formData.site || 'ms'} onValueChange={(value) => setFormData(prev => ({ ...prev, site: value }))}>
            <SelectTrigger className="rounded-xl" data-testid="class-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ms">{SITE_NAMES.ms}</SelectItem>
              <SelectItem value="ds">{SITE_NAMES.ds}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <div className="font-medium text-[#2C3E50]">Aktivní trída</div>
            <div className="text-sm text-[#64748B]">Zobrazit na webu</div>
          </div>
          <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))} data-testid="class-active-switch" />
        </div>

        <div className="flex gap-4 pt-4">
          <Button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="class-save">
            {saving ? 'Ukládání...' : (isEdit ? 'Uložit změny' : 'Vytvořit třídu')}
          </Button>
          <Link to="/admin/tridy" className="flex-1">
            <Button type="button" variant="outline" className="w-full rounded-full">Zrušit</Button>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ClassesListAdmin;
